import { useState, useEffect } from 'react';
import { Search, Mic, Camera, ScreenShare, Disc, Headphones, Laptop } from 'lucide-react';
import { useCurrentUser } from '../context/authHooks';
import { useMediaStore } from '../shared/store/useMediaStore';
import { useScreenRecordStore } from '../shared/media/useScreenRecordStore';
import { useCurrentUserPermissions } from '../features/spaces/hooks/useSpaceDashboard';
import { useMapBridge } from '../shared/store/useMapBridge';
import './MenuMedia.css';
import Searcher from '../shared/searcher/Searcher';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: destello de error de guardado disparado por `useMapBridge` (con cleanup de timer) y reseteo del cronómetro de grabación al detenerse.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const MenuMedia = ({ isBuzzing }) => {
    // 03.1-auth-store.md §6 grupo 2 -- solo usa `user.name`, dentro del alcance angosto de
    // `useCurrentUser()`.
    const { name: userName } = useCurrentUser();
    const { permissions } = useCurrentUserPermissions();

    // Screen recorder + audio-input picker — shell chrome reads the stores directly (§1a).
    // These used to ride Home's `mediaActions` prop; `Home` no longer has that state.
    const recording = useScreenRecordStore((s) => s.recording);
    const micEnabled = useScreenRecordStore((s) => s.micEnabled);
    const audioDeviceId = useMediaStore((s) => s.audioDeviceId);
    // Local state removed, using props from Home
    const [elapsedTime, setElapsedTime] = useState(0);
    const [audioDevices, setAudioDevices] = useState([]);
    const [isAudioMenuOpen, setIsAudioMenuOpen] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [isSaveError, setIsSaveError] = useState(false);

    // global-save-pulse / global-save-pulse-error (paso a4): antes CustomEvents de `window`,
    // ahora un selector de `useMapBridge` -- este efecto conserva los mismos timers locales
    // (800ms/2000ms) de siempre, solo cambia el disparador. `saveStatus` es un objeto nuevo en
    // cada `pulseMapSave`/`pulseMapSaveError` (aunque el contenido sea igual), así que el
    // efecto se re-dispara en cada pulso -- mismo comportamiento que el CustomEvent, que
    // también disparaba por cada dispatch sin importar el valor previo.
    const saveStatus = useMapBridge((s) => s.saveStatus);
    useEffect(() => {
        if (!saveStatus) return;
        if (saveStatus.error) {
            setIsSaving(true);
            setIsSaveError(true);
            const t = setTimeout(() => { setIsSaving(false); setIsSaveError(false); }, 2000);
            return () => clearTimeout(t);
        }
        if (saveStatus.saving) {
            setIsSaving(true);
            setIsSaveError(false);
            const t = setTimeout(() => setIsSaving(false), 800);
            return () => clearTimeout(t);
        }
    }, [saveStatus]);

    // map-groups-update (paso a4): antes un CustomEvent de `window` sincronizado a useState
    // local, ahora derivado directo del selector en cada render -- sin efecto intermedio
    // (rerender-derived-state-no-effect), mismo resultado (`[]` antes del primer map-update).
    const mapGroups = useMapBridge((s) => s.mapGroups);
    const watchedSessionTerminalId = useMapBridge((s) => s.watchedSessionTerminalId);
    const watchedSessionParticipants = useMapBridge((s) => s.watchedSessionParticipants) || [];
    const remoteCameraStreams = useMapBridge((s) => s.remoteCameraStreams);
    const liveGroups = mapGroups?.groups || [];
    const liveIsolated = mapGroups?.isolated || [];

    // mic/cam/share (estado on/off + normal|pending|error) viven en useMediaStore, no en un
    // useState local alimentado por CustomEvent -- el store ya es reactivo y maneja su propio
    // guard de "no pisar error con normal" + timer de 3s.
    const mic = useMediaStore((state) => state.mic);
    const cam = useMediaStore((state) => state.cam);
    const share = useMediaStore((state) => state.share);
    const mediaState = { mic: mic.status, cam: cam.status, share: share.status };
    const activeMedia = { mic: mic.requested, cam: cam.requested, share: share.requested };

    const getDeviceIcon = (deviceLabel, size = 12, color = '#fff') => {
        const label = (deviceLabel || '').toLowerCase();
        if (label.includes('airpods') || label.includes('apple') || label.includes('headphone') || label.includes('earbud')) {
            return <Headphones size={size} color={color} />;
        }
        if (label.includes('macbook') || label.includes('display') || label.includes('computer') || label.includes('laptop') || label.includes('built-in')) {
            return <Laptop size={size} color={color} />;
        }
        return <Mic size={size} color={color} />;
    };

    useEffect(() => {
        const getDevices = async () => {
            try {
                const devs = await navigator.mediaDevices.enumerateDevices();
                const audioInputs = devs.filter(d => d.kind === 'audioinput');
                setAudioDevices(audioInputs);

                // Seed the store with the first input if nothing's chosen yet.
                if (audioInputs.length > 0 && !useMediaStore.getState().audioDeviceId) {
                    useMediaStore.getState().setAudioDeviceId(audioInputs[0].deviceId);
                }
            } catch (e) {
                console.error("Error enumerating devices", e);
            }
        };
        getDevices();
        navigator.mediaDevices.addEventListener('devicechange', getDevices);
        return () => navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    }, [audioDeviceId]);

    useEffect(() => {
        let timer;
        if (recording) {
            timer = setInterval(() => {
                setElapsedTime(prev => prev + 1);
            }, 1000);
        } else {
            setElapsedTime(0);
        }
        return () => clearInterval(timer);
    }, [recording]);

    const formatTime = (totalSeconds) => {
        const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const s = (totalSeconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const toggleAction = (key) => {
        if (mediaState[key] === 'pending') return; // ya hay una conexión/desconexión en curso para esta acción

        if (key === 'rec') {
            useScreenRecordStore.getState().toggle();
            return;
        }
        if (key === 'rec_audio') {
            useScreenRecordStore.getState().setMicEnabled(!micEnabled);
            return;
        }

        // mic/cam/share togglean en useMediaStore.
        if (key === 'mic' || key === 'cam' || key === 'share') {
            useMediaStore.getState().toggleRequested(key);
        }
    };

    return (
        <header className="menumedia">
            <div className="media-left-section">
                {permissions?.nav_search !== false && <Searcher />}

                <div className="media-actions-container">
                    <div className="mic-action-wrapper" style={{ position: 'relative' }}>
                        <button
                            className={`action-btn ${activeMedia.mic ? 'active' : ''} ${isBuzzing && !activeMedia.mic ? 'buzzing' : ''} ${mediaState.mic === 'pending' ? 'pending' : ''} ${mediaState.mic === 'error' ? 'error' : ''}`}
                            onClick={() => toggleAction('mic')}
                            disabled={mediaState.mic === 'pending'}
                            title={mediaState.mic === 'pending' ? 'Conectando...' : mediaState.mic === 'error' ? 'No se pudo conectar' : undefined}
                        >
                            <Mic size={20} color={activeMedia.mic ? '#000' : '#FFF'} />
                        </button>

                        {/* Pequeño circulo para cambiar de entrada */}
                        <div
                            className="audio-device-trigger"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsAudioMenuOpen(!isAudioMenuOpen);
                            }}
                        >
                            {(() => {
                                const selectedDev = audioDevices.find(d => d.deviceId === audioDeviceId);
                                return getDeviceIcon(selectedDev?.label, 12, '#fff');
                            })()}
                        </div>

                        {/* Menú Desplegable */}
                        {isAudioMenuOpen && (
                            <div className="audio-device-dropdown">
                                <div className="audio-dropdown-header">Entradas de Audio</div>
                                {audioDevices.length === 0 && (
                                    <div className="audio-dropdown-header" style={{ textTransform: 'none', color: '#777' }}>Mínimo habla una vez para detectar...</div>
                                )}
                                {audioDevices.map((device, idx) => (
                                    <div
                                        key={device.deviceId || idx}
                                        className={`audio-device-option ${audioDeviceId === device.deviceId ? 'selected' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            useMediaStore.getState().setAudioDeviceId(device.deviceId);
                                            setIsAudioMenuOpen(false);
                                        }}
                                    >
                                        <div className="device-icon">
                                            {getDeviceIcon(device.label, 16, audioDeviceId === device.deviceId ? '#39FF14' : '#fff')}
                                        </div>
                                        <div className="device-name">
                                            {device.label || `Micrófono ${idx + 1}`}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        className={`action-btn ${activeMedia.cam ? 'active' : ''} ${mediaState.cam === 'pending' ? 'pending' : ''} ${mediaState.cam === 'error' ? 'error' : ''}`}
                        onClick={() => toggleAction('cam')}
                        disabled={mediaState.cam === 'pending'}
                        title={mediaState.cam === 'pending' ? 'Conectando...' : mediaState.cam === 'error' ? 'No se pudo conectar' : undefined}
                    >
                        <Camera size={20} color={activeMedia.cam ? '#000' : '#FFF'} />
                    </button>
                    <button
                        className={`action-btn ${activeMedia.share ? 'active' : ''} ${mediaState.share === 'pending' ? 'pending' : ''} ${mediaState.share === 'error' ? 'error' : ''}`}
                        onClick={() => toggleAction('share')}
                        disabled={mediaState.share === 'pending'}
                        title={mediaState.share === 'pending' ? 'Conectando...' : mediaState.share === 'error' ? 'No se pudo conectar' : undefined}
                    >
                        <ScreenShare size={20} color={activeMedia.share ? '#000' : '#FFF'} />
                    </button>
                </div>

                {permissions?.nav_video !== false && (
                    <div className="rec-action-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <button
                            className={`action-btn ${recording ? 'active-rec' : ''}`}
                            onClick={() => toggleAction('rec')}
                            title={recording ? "Detener Grabación" : "Grabar Pantalla"}
                        >
                            <Disc size={20} color={recording ? '#ef4444' : '#FFF'} fill={recording ? '#ef4444' : 'none'} />
                        </button>

                        {/* Mic Sub-button during recording */}
                        {recording && (
                            <button
                                className="action-btn-small"
                                style={{
                                    backgroundColor: micEnabled ? '#ef4444' : '#666',
                                    border: micEnabled ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid #333333',
                                    padding: '4px',
                                    width: '24px', height: '24px',
                                    marginLeft: '4px',
                                    borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAction('rec_audio');
                                }}
                                title={micEnabled ? "Micrófono Activado" : "Micrófono Silenciado"}
                            >
                                {micEnabled ? <Mic size={14} color="#FFF" /> : <Mic size={14} color="#ccc" />}
                            </button>
                        )}
                        {recording && (
                            <div className="recording-timer">
                                {String(Math.floor(elapsedTime / 60)).padStart(2, '0')}:
                                {String(elapsedTime % 60).padStart(2, '0')}
                            </div>
                        )}
                    </div>
                )}

                {/* Cámaras Activas de la Sesión de Terminal Observada */}
                {watchedSessionTerminalId && (
                    <div className="watched-session-cameras" style={{ display: 'flex', alignItems: 'center', marginLeft: '15px', gap: '8px' }}>
                        {(() => {
                            if (watchedSessionParticipants.length === 0) return null;

                            return watchedSessionParticipants.map(u => {
                                const stream = remoteCameraStreams[u.id];
                                return (
                                    <div key={u.id} style={{
                                        width: '44px', height: '44px', borderRadius: '8px', 
                                        overflow: 'hidden', border: '2px solid #22c55e',
                                        backgroundColor: '#1e1e1e', position: 'relative',
                                        boxShadow: '0 0 12px rgba(34, 197, 94, 0.4)'
                                    }} title={u.name || u.nick}>
                                        {stream ? (
                                            <video 
                                                autoPlay 
                                                playsInline 
                                                muted 
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                ref={(el) => {
                                                    if (el && el.srcObject !== stream) {
                                                        el.srcObject = stream;
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <img src={u.image || u.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || u.nick)}&background=random`} alt={u.name || u.nick} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }} />
                                        )}
                                        <div style={{
                                            position: 'absolute', bottom: '0', left: '0', right: '0',
                                            background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '9px',
                                            textAlign: 'center', padding: '2px 0', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap'
                                        }}>
                                            {u.nick}
                                        </div>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                )}



                {/* Status Indicator de Usuarios Conectados */}
                {(liveGroups.length > 0 || liveIsolated.length > 0) && (
                    <div className="live-users-mesh" style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '20px',
                        gap: '7px'
                    }}>
                        {/* 1. Groups (verde = proximidad, morado = zona de conversación de
                            terminal — integración main→dev epic C3). El shape del grupo pasó de
                            un array pelado a `{ members, isTerminalZone }`. */}
                        {liveGroups.map((group, gIndex) => (
                            <div key={`group-${gIndex}`} className="live-user-group" style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '7px',
                                padding: '2px 4px',
                                borderRadius: '16px',
                                backgroundColor: group.isTerminalZone ? 'rgba(147, 89, 255, 0.2)' : 'rgba(57, 255, 20, 0.2)',
                                border: `1px solid ${group.isTerminalZone ? 'rgba(147, 89, 255, 0.45)' : 'rgba(57, 255, 20, 0.4)'}`
                            }}>
                                {group.members.map((u) => (
                                    <div
                                        key={u.id}
                                        className="top-avatar-wrapper"
                                        data-tooltip={u.nick}
                                        title="Doble-click para caminar hacia esta persona"
                                        onDoubleClick={() => u.x != null && useMapBridge.getState().requestWalkTo({ x: u.x, y: u.y, userId: u.id })}
                                        style={{ position: 'relative', width: '35px', height: '35px', cursor: u.x != null ? 'pointer' : 'default' }}
                                    >
                                        <img
                                            src={u.avatar}
                                            alt={u.nick}
                                            loading="lazy"
                                            style={{
                                                width: '35px',
                                                height: '35px',
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                        {u.micOn && (
                                            <div style={{
                                                position: 'absolute', bottom: -2, right: -2,
                                                backgroundColor: '#22c55e', borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                border: '1.5px solid #1e1e1e', width: '14px', height: '14px'
                                            }}>
                                                <Mic size={9} color="#fff" strokeWidth={3} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}

                        {/* 2. Isolated Users */}
                        {liveIsolated.map((u) => (
                            <div
                                key={u.id}
                                className="top-avatar-wrapper"
                                data-tooltip={u.nick}
                                title="Doble-click para caminar hacia esta persona"
                                onDoubleClick={() => u.x != null && useMapBridge.getState().requestWalkTo({ x: u.x, y: u.y, userId: u.id })}
                                style={{ position: 'relative', width: '35px', height: '35px', cursor: u.x != null ? 'pointer' : 'default' }}
                            >
                                <img
                                    src={u.avatar}
                                    alt={u.nick}
                                    loading="lazy"
                                    style={{
                                        width: '35px',
                                        height: '35px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                                {u.micOn && (
                                    <div style={{
                                        position: 'absolute', bottom: -2, right: -2,
                                        backgroundColor: '#22c55e', borderRadius: '50%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: '1.5px solid #1e1e1e', width: '14px', height: '14px'
                                    }}>
                                        <Mic size={9} color="#fff" strokeWidth={3} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Donut Saving Indicator */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingRight: '20px' }}>
                <div className={`global-save-donut ${isSaving ? 'pulsing' : ''} ${isSaveError ? 'error' : ''}`}></div>
            </div>
        </header>
    );
};

export default MenuMedia;
